import { useEffect, useRef, useState } from "react";
import { formatXOF } from "@/lib/format";
import type { ProductListItem } from "@/lib/api-client";

const SEEN_KEY = "vvs_promo_modal_seen";
/** Délai après le chargement COMPLET de la page (événement `load`, images comprises). */
const OPEN_DELAY_MS = 3000;

export interface PromoWhatsAppModalProps {
  products: ProductListItem[];
  /** Lien wa.me du vendeur (`getWhatsAppContact().link`) — "#" si le numéro est indisponible. */
  whatsAppLink: string;
}

/**
 * Modal d'accueil « Quel produit t'intéresse ? » — campagne d'acquisition : les prospects
 * redirigés vers l'accueil voient, 3 s après le chargement complet, les produits en promo /
 * en pub ; un clic ouvre la conversation WhatsApp du vendeur avec un message pré-rempli
 * (nom, prix, lien de la fiche produit).
 *
 * Affiché une seule fois par session de navigation (`sessionStorage`) pour ne pas gêner un
 * visiteur qui revient sur l'accueil pendant sa visite. Dégradation silencieuse si le
 * stockage est inaccessible (le modal s'affiche alors à chaque arrivée sur l'accueil).
 */
export function PromoWhatsAppModal({ products, whatsAppLink }: PromoWhatsAppModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const isEnabled = products.length > 0 && whatsAppLink !== "#";

  useEffect(() => {
    if (!isEnabled) return;

    try {
      if (window.sessionStorage.getItem(SEEN_KEY) === "1") return;
    } catch {
      // Stockage indisponible : on affiche quand même.
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      timer = setTimeout(() => setIsOpen(true), OPEN_DELAY_MS);
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }

    return () => {
      window.removeEventListener("load", schedule);
      if (timer) clearTimeout(timer);
    };
  }, [isEnabled]);

  useEffect(() => {
    if (!isOpen) return;

    try {
      window.sessionStorage.setItem(SEEN_KEY, "1");
    } catch {
      // Sans gravité.
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setIsOpen(false);
    }
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Largeur du modal et nombre de colonnes adaptés au nombre de produits : pas de grand
  // modal à moitié vide pour 1-2 produits sur ordinateur.
  const count = products.length;
  const dialogWidth = count <= 2 ? "md:max-w-lg" : count <= 6 ? "md:max-w-2xl" : "md:max-w-4xl";
  const gridCols =
    count === 1
      ? "grid-cols-1 max-w-[240px] mx-auto w-full"
      : count <= 2
        ? "grid-cols-2"
        : count <= 6
          ? "grid-cols-2 sm:grid-cols-3"
          : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";

  function buildWhatsAppHref(product: ProductListItem): string {
    const productUrl = `${window.location.origin}/p/${product.slug}`;
    const message = [
      "Bonjour, je suis intéressé(e) par ce produit :",
      `*${product.name}*`,
      `Prix : ${formatXOF(product.priceMin.amountMinor)}`,
      productUrl,
    ].join("\n");
    return `${whatsAppLink}?text=${encodeURIComponent(message)}`;
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 p-0 md:items-center md:p-6"
      onClick={() => setIsOpen(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-modal-title"
        className={[
          // Mobile : panneau qui monte du bas (pouce), hauteur en `dvh` pour tenir compte de la
          // barre d'adresse mobile ; tablette/ordinateur : fenêtre centrée.
          "flex max-h-[88dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-cream shadow-xl md:max-h-[85vh] md:rounded-lg",
          dialogWidth,
        ].join(" ")}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Poignée visuelle du panneau mobile. */}
        <div aria-hidden="true" className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-line md:hidden" />
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-line px-4 py-3 md:px-6 md:py-4">
          <div className="min-w-0">
            <p className="font-body text-xs font-semibold uppercase tracking-wide text-gold">
              Offres du moment
            </p>
            <h2 id="promo-modal-title" className="font-display text-lg text-ink sm:text-xl md:text-2xl">
              Quel produit t&apos;intéresse ?
            </h2>
            <p className="mt-1 font-body text-xs text-muted sm:text-sm">
              Choisis un produit, on te répond directement sur WhatsApp.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label="Fermer"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-3xl leading-none text-muted transition-colors hover:bg-cream2 hover:text-ink"
          >
            &times;
          </button>
        </div>

        <ul
          className={[
            "grid gap-3 overflow-y-auto overscroll-contain p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:gap-4 sm:p-4 md:p-6",
            gridCols,
          ].join(" ")}
        >
          {products.map((product) => {
            const images = product.media.filter((m) => m.type === "IMAGE");
            const image = images.find((m) => m.isPrimary) ?? images[0] ?? null;
            const compareAt = product.compareAtPrice?.amountMinor ?? null;
            const hasDiscount = compareAt !== null && compareAt > product.priceMin.amountMinor;

            return (
              <li key={product.id}>
                <a
                  href={buildWhatsAppHref(product)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="group flex h-full flex-col overflow-hidden rounded-md border border-line bg-white transition-shadow hover:shadow-md"
                >
                  <div className="relative aspect-square w-full overflow-hidden bg-cream2">
                    {hasDiscount ? (
                      <span className="absolute left-2 top-2 z-10 rounded-sm bg-gold px-1.5 py-0.5 font-body text-[10px] font-semibold uppercase text-navy">
                        Promo
                      </span>
                    ) : null}
                    {image ? (
                      <img
                        src={image.url}
                        alt={image.alt ?? product.name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                      />
                    ) : null}
                  </div>
                  <div className="flex flex-1 flex-col gap-1 p-2 sm:p-3">
                    <h3 className="line-clamp-2 font-body text-xs font-medium text-ink sm:text-sm">
                      {product.name}
                    </h3>
                    <p className="flex flex-wrap items-baseline gap-x-1.5 font-body">
                      <span className="text-sm font-semibold text-ink sm:text-base">
                        {formatXOF(product.priceMin.amountMinor)}
                      </span>
                      {hasDiscount ? (
                        <span className="text-xs text-muted line-through">{formatXOF(compareAt)}</span>
                      ) : null}
                    </p>
                    <span className="mt-auto flex min-h-[36px] items-center justify-center rounded-sm bg-[#25D366] px-2 py-1.5 text-center font-body text-[11px] font-semibold leading-tight text-white transition-colors group-hover:bg-[#1ebe5b] sm:text-xs">
                      Je suis intéressé(e)
                    </span>
                  </div>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
